import type { MouseEvent, PropsWithChildren } from 'react';
import { forwardRef, useCallback, useRef } from 'react';

/* eslint-disable no-restricted-imports -- This file is the canonical cake-tracked atoms wrapper; all other cake code must import these names from `platform/atoms`. */
import { Button as AtomsButton } from '@opendoor/atoms/button';
import type { ButtonProps as AtomsButtonProps } from '@opendoor/atoms/button';
import { Link as AtomsLink } from '@opendoor/atoms/link';
import type { LinkProps as AtomsLinkProps } from '@opendoor/atoms/link';
import { Toasty } from '@opendoor/atoms/toast';
/* eslint-enable no-restricted-imports */
import type { AtomsProviderProps, LinkRenderProps, RenderLink } from '@opendoor/atoms/utils';
import { AtomsPortalProvider, AtomsProvider, LinkProvider } from '@opendoor/atoms/utils';
import { SystemError } from 'platform/Error';
import { useObservability } from 'platform/observability';
import { useInternalCTAProps } from 'platform/router/internal';

const renderCakeAtomsLink: RenderLink = (props) => <CakeAtomsLink {...props} />;

const CakeAtomsLink = forwardRef<HTMLAnchorElement, LinkRenderProps>(function CakeAtomsLink(
  { href: to, to: fallbackTo, onClick, onKeyDown, target, ...rest },
  ref,
) {
  const targetPath = to ?? fallbackTo;

  if (!targetPath) {
    throw new SystemError('Atoms link must have a properly formatted `href` prop');
  }

  const handleAtomsPress = useCallback(
    (event: MouseEvent<HTMLAnchorElement>) => {
      // Cake's CTA hook runs this inside its press pipeline; forward to the caller's anchor onClick.
      onClick?.(event);
    },
    [onClick],
  );

  const { href, onPress } = useInternalCTAProps<MouseEvent<HTMLAnchorElement>>({
    href: targetPath,
    onPress: handleAtomsPress,
    target,
  });

  const handleClick = useCallback(
    (event: MouseEvent<HTMLAnchorElement>) => {
      onPress?.(event);
    },
    [onPress],
  );

  const handleKeyDown: LinkRenderProps['onKeyDown'] = useCallback(
    (event) => {
      onKeyDown?.(event);

      if (event.defaultPrevented || event.key !== ' ') {
        return;
      }

      event.preventDefault();
      event.currentTarget.click();
    },
    [onKeyDown],
  );

  return (
    // eslint-disable-next-line @opendoor/no-html-elements, jsx-a11y/anchor-has-content -- Cake's Atoms LinkProvider renderer must output the host anchor after applying Cake routing checks.
    <a
      {...rest}
      href={href}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      ref={ref}
      target={target}
    />
  );
});

type CakeAtomsProviderProps = PropsWithChildren<{
  useAtomsObservability?: AtomsProviderProps['useObservability'];
}>;

export const CakeAtomsProvider = ({
  children,
  useAtomsObservability = useObservability,
}: CakeAtomsProviderProps) => {
  // Atoms overlays (Dialog/Combobox/Toast) portal to <body> at z-auto, so app
  // chrome that owns a stacking context — the global header, the offer card's
  // `sticky top-0 z-10` header — paints over the backdrop. Route overlays into a
  // high-z-index root so they always sit above page content (mirrors cosmos).
  const atomsOverlayRoot = useRef<HTMLDivElement>(null);

  return (
    <AtomsProvider useObservability={useAtomsObservability}>
      <LinkProvider renderLink={renderCakeAtomsLink}>
        {/* Zero-size portal target for atoms overlays; portaled children position themselves. */}
        {/* eslint-disable-next-line @opendoor/no-html-elements -- infra portal root, not UI content */}
        <div
          id="atoms-overlay-root"
          ref={atomsOverlayRoot}
          className="fixed top-0 left-0 z-[100000]"
        />
        <AtomsPortalProvider container={atomsOverlayRoot}>
          {/* Toasty's viewport portals through the AtomsPortalProvider container
              above, so toasts sit in the same high-z overlay root as dialogs. */}
          <Toasty>{children}</Toasty>
        </AtomsPortalProvider>
      </LinkProvider>
    </AtomsProvider>
  );
};

// Re-export atoms click-tracked components with `analyticsName` required so
// every cake invocation emits a `linkClickEvent` / `buttonClickEvent`. The
// rendered anchor still routes through `CakeAtomsLink` (see `LinkProvider`
// above) for internal CTA handling.

type WithRequiredAnalyticsName<T> = T extends unknown
  ? Omit<T, 'analyticsName'> & { analyticsName: string }
  : never;

export type LinkProps = WithRequiredAnalyticsName<AtomsLinkProps>;
export const Link = forwardRef<HTMLAnchorElement, LinkProps>(function Link(props, ref) {
  return <AtomsLink {...props} ref={ref} />;
});

export type ButtonProps = WithRequiredAnalyticsName<AtomsButtonProps>;
export const Button = forwardRef<HTMLButtonElement | HTMLAnchorElement, ButtonProps>(
  function Button(props, ref) {
    return <AtomsButton {...props} ref={ref} />;
  },
);
